import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, ExternalLink, Radio, Heart } from "lucide-react";
import FollowHostButton from "@/components/FollowHostButton";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function initials(name) {
  return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

export default function HostDetail() {
  const { name } = useParams();
  const [host, setHost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/hosts/${encodeURIComponent(name)}`);
        setHost(data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [name]);

  if (notFound) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24" data-testid="host-not-found">
        <h1 className="font-display text-5xl mb-4">HOST NOT FOUND.</h1>
        <p className="text-[var(--ff-text-dim)] mb-8">We couldn't find a host with that name on the roster.</p>
        <Link to="/hosts" className="btn-primary"><ArrowLeft size={14} /> Back to Hosts</Link>
      </div>
    );
  }
  if (!host) return <div className="p-12 text-[var(--ff-text-dim)]">Loading...</div>;

  // Sort shows by day of week then start time
  const sortedShows = [...host.shows].sort((a, b) => {
    if (a.day_of_week !== b.day_of_week) return a.day_of_week - b.day_of_week;
    return a.start_time.localeCompare(b.start_time);
  });

  return (
    <div data-testid="host-detail-page" className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24">
      <Link to="/hosts" className="inline-flex items-center gap-2 label-eyebrow mb-8 hover:text-[var(--ff-red)]" data-testid="back-to-hosts">
        <ArrowLeft size={12} /> BACK TO HOSTS
      </Link>

      <div className="grid lg:grid-cols-12 gap-12 mb-20">
        <div className="lg:col-span-4">
          {host.avatar_url ? (
            <img src={host.avatar_url} alt={host.name} className="w-full aspect-square object-cover" />
          ) : (
            <div className="w-full aspect-square bg-[var(--ff-red)] text-white flex items-center justify-center font-display text-[10rem] leading-none">
              {initials(host.name)}
            </div>
          )}
        </div>
        <div className="lg:col-span-8">
          {host.title && <div className="label-eyebrow mb-4 text-[var(--ff-red)]">▮ {host.title.toUpperCase()}</div>}
          <h1 className="font-display text-5xl lg:text-8xl mb-6">{host.name}</h1>
          {host.bio ? (
            <p className="text-lg text-[var(--ff-text-dim)] leading-relaxed max-w-3xl mb-6" data-testid="host-bio">{host.bio}</p>
          ) : (
            <p className="text-lg text-[var(--ff-text-dim)] italic mb-6">No bio provided yet.</p>
          )}
          {host.social_url && (
            <a href={host.social_url} target="_blank" rel="noreferrer" className="btn-ghost inline-flex" data-testid="host-social-link">
              <ExternalLink size={14} /> Social
            </a>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <FollowHostButton hostName={host.name} />
            <Link to={`/donate/${host.id}`} className="btn-primary inline-flex" data-testid="host-donate-btn">
              <Heart size={14} /> Send a Donation
            </Link>
          </div>
        </div>
      </div>

      <div className="border-t border-[var(--ff-border)] pt-12">
        <div className="flex items-center gap-3 mb-8">
          <Radio className="text-[var(--ff-red)]" size={24} />
          <h2 className="font-display text-4xl">{host.name.split(" ")[0]}'s Schedule</h2>
        </div>
        {sortedShows.length === 0 ? (
          <div className="text-[var(--ff-text-dim)] italic">No shows currently scheduled.</div>
        ) : (
          <div className="space-y-px bg-[var(--ff-border)]">
            {sortedShows.map((s) => (
              <div key={s.id} className="bg-[var(--ff-surface)] p-6 grid md:grid-cols-12 gap-4 items-center hover:bg-[var(--ff-surface-hover)] transition-colors" data-testid={`host-show-${s.id}`}>
                <div className="md:col-span-1 font-mono-ui text-xs uppercase tracking-[0.15em] text-[var(--ff-red)]">
                  {DAY_NAMES[s.day_of_week].slice(0, 3).toUpperCase()}
                </div>
                <div className="md:col-span-2 font-mono-ui text-sm text-[var(--ff-text-dim)]">
                  {s.start_time}–{s.end_time}
                </div>
                <div className="md:col-span-6">
                  <div className="font-display text-xl">{s.title}</div>
                  {s.description && <div className="text-xs text-[var(--ff-text-dim)] mt-1">{s.description}</div>}
                </div>
                <div className="md:col-span-2">
                  {s.category && (
                    <span className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] px-2 py-1 border border-[var(--ff-border)]">
                      {s.category}
                    </span>
                  )}
                </div>
                <div className="md:col-span-1 text-right">
                  {s.youtube_url && (
                    <a href={s.youtube_url} target="_blank" rel="noreferrer" className="text-[var(--ff-red)] inline-block" data-testid={`host-show-youtube-${s.id}`}>
                      <ExternalLink size={16} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
